<template>
  <div class="details flex flex-col gap-2 md:flex-row">
    <div class="meta w-full md:w-1/4">
      <picture class="show-image">
        <source
          class="show-image-mobile-source"
          :srcset="mobileImgSrc"
          type="image/webp"
          media="(max-width: 768px)"
        />
        <source class="show-image-desktop-source" :srcset="desktopImgSrc" type="image/webp" />
        <img
          :src="show.srcset.original.src"
          :alt="show.name"
          class="w-full h-auto object-cover"
          :class="imageAspectClass"
          :loading="isPriority ? 'eager' : 'lazy'"
          :fetchpriority="isPriority ? 'high' : 'auto'"
          decoding="async"
        />
      </picture>
    </div>

    <div class="performances whitespace-pre-line w-full md:w-3/4">
      <div class="flex justify-start items-center">
        <component
          :is="`h${headingLevel}`"
          class="text-3xl font-semibold font-heading mr-auto"
          :id="`show-${show.id}-name`"
        >
          {{ show.name }}
        </component>

        <router-link
          :id="`residency-link-${show.id}`"
          class="residency-button mr-2 py-1 px-3 rounded-full border border-current text-xs"
          v-if="firstResidencySlug"
          :to="{ name: 'residency-hub', params: { slug: firstResidencySlug } }"
          :aria-label="`View ${show.name} residency`"
        >
          Residency
        </router-link>

        <router-link
          v-show="descriptionDisplayType === 'short'"
          class="show-link"
          :id="`show-${show.id}-date-${show.date}`"
          :to="{
            name: 'showDetails',
            params: { showId: show.id },
            query: { date: show.date },
          }"
          @click="emit('showLink:click', show)"
          :aria-label="`View details for ${show.name} show on ${settingStore.formatTemporal(show.date, 'MMMM DD, YYYY')}`"
        >
          <LinkIcon class="w-6 h-6" />
        </router-link>
      </div>

      <div class="uppercase text-base font-semibold mb-3 font-date text-secondary">
        <component
          :is="`h${dateHeadingLevel}`"
          class="day-of-week"
          :id="`show-${show.id}-${show.date}-desc`"
        >
          {{ show.dateStr }}
        </component>
      </div>

      <div
        class="relative text-sm md:text-base font-sans"
        :id="`show-${show.id}-description`"
        ref="descriptionContainer"
        :class="descriptionDisplayType === 'short' ? 'line-clamp-[18]' : 'line-clamp-none'"
      >
        <div class="whitespace-pre-wrap" v-html="sanitizedDescription" />
        <DebouncedButton
          v-if="displayReadMoreText || keepReadMoreText"
          class="read-more inline-block bg-body-primary absolute pl-2 bottom-0 right-0 text-button-guest-primary underline decoration-button-guest-primary"
          @click="emit('readMore:click', show)"
          :class="{ 'relative inline pl-0': descriptionDisplayType === 'long' }"
          :aria-label="`Read more about ${show.name} show description`"
        >
          <slot name="readMoreText">Read More</slot>
        </DebouncedButton>
      </div>

      <div
        class="flex flex-wrap justify-start items-start gap-2 pt-4 pb-4 border-t border-linear-g-primary mt-4 md:border-t-0 md:mt-0 md:pb-0 bg-body-primary sticky bottom-0 sm:gap-4 md:bg-transparent md:relative"
      >
        <PerformanceButton
          class="performance-btn"
          v-for="performance in show.performances"
          :key="performance.id"
          :forcePurchase="performance.forcePurchase"
          :performance="performance"
          :error="ui.error"
          :buttonClasses="`performance-${performance.id}`"
          @click="emit('performance:click', performance)"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { useResizeObserver } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, nextTick, onMounted, ref, toRefs, watch } from 'vue'

import LinkIcon from '@/assets/link.svg?component'
import { useFeatureFlags } from '@/common/composables/useFeatureFlags'
import { StorePerformance } from '@/models'
import DebouncedButton from '@/modules/guestUi/components/atoms/DebouncedButton.vue'
import PerformanceButton from '@/modules/guestUi/components/organisms/PerformanceButton.vue'
import { DisplayShow } from '@/modules/guestUi/interfaces/shows'
import { useSettingStore } from '@/stores/setting'
import { ErrorData } from '@/utils/errors'
import { sanitizeHtmlForClient } from '@/utils/sanitize'

const emit = defineEmits<{
  // eslint-disable-next-line no-unused-vars
  (e: 'performance:click', performance: StorePerformance): void
  // eslint-disable-next-line no-unused-vars
  (e: 'showLink:click', show: DisplayShow): void
  // eslint-disable-next-line no-unused-vars
  (e: 'readMore:click', show: DisplayShow): void
}>()

type HeadingLevel = 1 | 2 | 3 | 4 | 5 | 6
const props = withDefaults(
  defineProps<{
    show: DisplayShow
    ui: { error: ErrorData }
    descriptionDisplayType?: 'long' | 'short'
    keepReadMoreText?: boolean
    headingLevel?: HeadingLevel
    isPriority?: boolean
  }>(),
  {
    descriptionDisplayType: 'short',
    keepReadMoreText: false,
    headingLevel: 3,
    isPriority: false,
  },
)
const { descriptionDisplayType, show, headingLevel } = toRefs(props)

const settingStore = useSettingStore()
const { image_ratio_mobile, image_ratio_desktop } = storeToRefs(settingStore)

const mobileImgSrc = computed<string>(() =>
  image_ratio_mobile?.value === 'square'
    ? show.value.srcset.sqXl.src
    : show.value.srcset.rectLg.src,
)
const desktopImgSrc = computed<string>(() =>
  image_ratio_desktop?.value === 'square'
    ? show.value.srcset.sqXl.src
    : show.value.srcset.rectLg.src,
)

// Reserve card image height (square 1:1 / rectangle 3:2, per breakpoint) so it can't shift on decode.
const imageAspectClass = computed<string[]>(() => [
  image_ratio_mobile?.value === 'square' ? 'aspect-square' : 'aspect-[3/2]',
  image_ratio_desktop?.value === 'square' ? 'md:aspect-square' : 'md:aspect-[3/2]',
])

const dateHeadingLevel = computed(
  (): HeadingLevel => Math.min(headingLevel.value + 1, 6) as HeadingLevel,
)
const descriptionContainer = ref<HTMLElement | null>(null)

const { isShowGridOptimizationEnabled } = useFeatureFlags()

const sanitizedDescription = computed<string>(() =>
  isShowGridOptimizationEnabled.value && show.value.sanitizedDescription !== undefined
    ? show.value.sanitizedDescription
    : sanitizeHtmlForClient(show.value.description),
)

const displayReadMoreTextLegacy = computed<boolean>(() => {
  if (!descriptionContainer.value) return false
  return descriptionContainer.value.scrollHeight > descriptionContainer.value.clientHeight
})

const displayReadMoreTextOptimized = ref<boolean>(false)

function measureReadMore() {
  const el = descriptionContainer.value
  if (!el) return
  displayReadMoreTextOptimized.value = el.scrollHeight > el.clientHeight
}

const { stop: stopResizeObserver } = useResizeObserver(descriptionContainer, () => {
  measureReadMore()
})

onMounted(() => {
  if (!isShowGridOptimizationEnabled.value || descriptionDisplayType.value === 'long') {
    stopResizeObserver()
    return
  }
  nextTick(measureReadMore)
})

watch(
  () => show.value.sanitizedDescription ?? show.value.description,
  () => {
    if (!isShowGridOptimizationEnabled.value) return
    if (descriptionDisplayType.value === 'long') return
    nextTick(measureReadMore)
  },
)

const displayReadMoreText = computed<boolean>(() =>
  isShowGridOptimizationEnabled.value
    ? displayReadMoreTextOptimized.value
    : displayReadMoreTextLegacy.value,
)

const firstResidencySlug = computed<string | undefined | null>(function () {
  if (isShowGridOptimizationEnabled.value && show.value.firstResidencySlug !== undefined) {
    return show.value.firstResidencySlug
  }
  return show.value.performances.flatMap(p => p.residencies)[0]?.slug
})
</script>
